import React, { useState, useMemo } from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet, Platform, LayoutAnimation, UIManager } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { DueDiligenceItem, ModuleCode, FlagType, ItemStatus, sortByFlagPriority, MODULE_CONFIG } from '../../shared/types/due-diligence';
import { ChecklistItem } from './ChecklistItem';
import { FlagBadge } from './FlagBadge';
import { Card } from '../ui';
import { Ionicons } from '@expo/vector-icons';

// Enable LayoutAnimation on Android
if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

export interface ChecklistBoardProps {
  items: DueDiligenceItem[];
  portfolioId: number;
  propertyId: number;
  onUpdateItem: (itemId: number, updates: {
    status?: ItemStatus;
    flag?: FlagType | null;
    notes?: string;
    dataFields?: Record<string, any>;
    assignedTo?: number[];
  }) => Promise<void>;
  availablePartners: Array<{ id: number; email: string; display_name?: string }>;
  loading?: boolean;
  viewMode?: 'all' | 'by_module' | 'by_flag' | 'by_status';
  filterFlag?: FlagType | null;
  filterStatus?: string;
  filterModule?: ModuleCode;
}

interface ModuleStats {
  total: number;
  applicable: number; // total - notApplicable
  complete: number;
  inProgress: number;
  notApplicable: number;
  pending: number;
  bigRedFlags: number;
  redFlags: number;
  yellowFlags: number;
  greenFlags: number;
}

function calculateModuleStats(items: DueDiligenceItem[]): ModuleStats {
  const stats = items.reduce((acc, item) => {
    acc.total++;
    if (item?.status === 'not_applicable') {
      acc.notApplicable++;
    } else {
      acc.applicable++;
      if (item?.status === 'complete') acc.complete++;
      if (item?.status === 'in_progress') acc.inProgress++;
      if (item?.status === 'pending' || !item?.status) acc.pending++;
    }
    if (item?.flag === 'big_red') acc.bigRedFlags++;
    if (item?.flag === 'red') acc.redFlags++;
    if (item?.flag === 'yellow') acc.yellowFlags++;
    if (item?.flag === 'green' || item?.flag === 'big_green') acc.greenFlags++;
    return acc;
  }, { total: 0, applicable: 0, complete: 0, inProgress: 0, notApplicable: 0, pending: 0, bigRedFlags: 0, redFlags: 0, yellowFlags: 0, greenFlags: 0 });
  
  return stats;
}

export function ChecklistBoard({
  items,
  portfolioId,
  propertyId,
  onUpdateItem,
  availablePartners,
  loading = false,
  viewMode = 'by_module',
  filterFlag,
  filterStatus,
  filterModule,
}: ChecklistBoardProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();
  const boardStyles = makeBoardStyles(tokens, utils, isMobile, isSmallMobile);

  // Track expanded modules and categories
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  // Safe items array
  const safeItems = items || [];
  
  // Filter items
  const filteredItems = useMemo(() => {
    let filtered = [...safeItems];
    
    if (filterFlag !== undefined) {
      filtered = filtered.filter(item => item?.flag === filterFlag);
    }
    
    if (filterStatus) {
      filtered = filtered.filter(item => item?.status === filterStatus);
    }
    
    if (filterModule) {
      filtered = filtered.filter(item => item?.module === filterModule);
    }
    
    return filtered;
  }, [safeItems, filterFlag, filterStatus, filterModule]);

  // Group items by module, then by category
  const groupedByModule = useMemo(() => {
    const grouped: Record<string, Record<string, DueDiligenceItem[]>> = {};
    
    filteredItems.forEach(item => {
      if (!item) return;
      const moduleKey = item.module || 'unknown';
      const categoryKey = item.category || 'general';
      
      if (!grouped[moduleKey]) {
        grouped[moduleKey] = {};
      }
      if (!grouped[moduleKey][categoryKey]) {
        grouped[moduleKey][categoryKey] = [];
      }
      grouped[moduleKey][categoryKey].push(item);
    });

    // Sort items within each category by flag priority
    Object.keys(grouped).forEach(moduleKey => {
      Object.keys(grouped[moduleKey]).forEach(categoryKey => {
        grouped[moduleKey][categoryKey] = sortByFlagPriority(grouped[moduleKey][categoryKey]);
      });
    });

    return grouped;
  }, [filteredItems]);

  const toggleModule = (moduleKey: string) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleKey)) {
        next.delete(moduleKey);
      } else {
        next.add(moduleKey);
      }
      return next;
    });
  };

  const toggleCategory = (categoryKey: string) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  const expandAll = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    const allModules = new Set(Object.keys(groupedByModule));
    const allCategories = new Set<string>();
    Object.entries(groupedByModule).forEach(([moduleKey, categories]) => {
      Object.keys(categories).forEach(categoryKey => {
        allCategories.add(`${moduleKey}-${categoryKey}`);
      });
    });
    setExpandedModules(allModules);
    setExpandedCategories(allCategories);
  };

  const collapseAll = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedModules(new Set());
    setExpandedCategories(new Set());
    setExpandedItem(null);
  };

  if (filteredItems.length === 0) {
    return (
      <Card padding="lg" elevated>
        <View style={boardStyles.emptyState}>
          <Ionicons 
            name="checkbox-outline" 
            size={48} 
            color={addColorOpacity(tokens.color.onSurface, 0.3)} 
          />
          <Text style={boardStyles.emptyText}>
            No checklist items found
          </Text>
          <Text style={boardStyles.emptySubtext}>
            {filterFlag || filterStatus || filterModule 
              ? 'Try adjusting your filters'
              : 'Initialize due diligence to create checklist items'}
          </Text>
        </View>
      </Card>
    );
  }

  const moduleKeys = Object.keys(groupedByModule).sort();

  return (
    <View style={boardStyles.container}>
      {/* Expand/Collapse All */}
      <View style={boardStyles.controlsRow}>
        <View style={boardStyles.summaryInfo}>
          <Text style={boardStyles.summaryText}>
            {filteredItems.length} items in {moduleKeys.length} modules
          </Text>
        </View>
        <View style={boardStyles.controlsButtons}>
          <Pressable onPress={expandAll} style={boardStyles.controlButton}>
            <Ionicons name="expand-outline" size={16} color={tokens.color.primary} />
            <Text style={boardStyles.controlButtonText}>Expand All</Text>
          </Pressable>
          <Pressable onPress={collapseAll} style={boardStyles.controlButton}>
            <Ionicons name="contract-outline" size={16} color={tokens.color.primary} />
            <Text style={boardStyles.controlButtonText}>Collapse All</Text>
          </Pressable>
        </View>
      </View>

      {/* Module Accordions */}
      {moduleKeys.map((moduleKey) => {
        const categories = groupedByModule[moduleKey];
        const moduleItems = Object.values(categories).flat();
        const stats = calculateModuleStats(moduleItems);
        const isExpanded = expandedModules.has(moduleKey);
        const moduleConfig = MODULE_CONFIG[moduleKey as ModuleCode];
        const moduleName = moduleConfig?.name || moduleKey.replace(/_/g, ' ');
        const moduleIcon = moduleConfig?.icon || 'folder-outline';

        return (
          <Card key={moduleKey} style={boardStyles.moduleCard} elevated>
            {/* Module Header */}
            <Pressable
              onPress={() => toggleModule(moduleKey)}
              style={({ pressed }) => [
                boardStyles.moduleHeader,
                pressed && boardStyles.moduleHeaderPressed,
              ]}
            >
              {isSmallMobile ? (
                // Stacked layout for small mobile
                <View style={{ flex: 1 }}>
                  {/* Row 1: Icon + Title + Chevron */}
                  <View style={boardStyles.moduleHeaderRow1}>
                    <View style={boardStyles.moduleIconContainer}>
                      <Ionicons 
                        name={moduleIcon as any} 
                        size={18} 
                        color={tokens.color.primary} 
                      />
                    </View>
                    <Text style={boardStyles.moduleTitle} numberOfLines={1}>{moduleName}</Text>
                    <Ionicons 
                      name={isExpanded ? 'chevron-up' : 'chevron-down'} 
                      size={18} 
                      color={tokens.color.onSurface} 
                    />
                  </View>
                  
                  {/* Row 2: Progress bar full width */}
                  <View style={boardStyles.progressContainerFull}>
                    <View style={boardStyles.progressBar}>
                      <View 
                        style={[
                          boardStyles.progressFill, 
                          { width: `${stats.applicable > 0 ? (stats.complete / stats.applicable) * 100 : 0}%` }
                        ]} 
                      />
                    </View>
                    <Text style={boardStyles.progressText}>
                      {stats.complete}/{stats.applicable}
                      {stats.notApplicable > 0 && ` (${stats.notApplicable} N/A)`}
                    </Text>
                  </View>
                  
                  {/* Row 3: Flag badges */}
                  <View style={boardStyles.flagRow}>
                    {stats.bigRedFlags > 0 && (
                      <View style={[boardStyles.flagPill, { backgroundColor: '#DC2626' }]}>
                        <Text style={boardStyles.flagPillText}>{stats.bigRedFlags} Big Red</Text>
                      </View>
                    )}
                    {stats.redFlags > 0 && (
                      <View style={[boardStyles.flagPill, { backgroundColor: '#EF4444' }]}>
                        <Text style={boardStyles.flagPillText}>{stats.redFlags} Red</Text>
                      </View>
                    )}
                    {stats.yellowFlags > 0 && (
                      <View style={[boardStyles.flagPill, { backgroundColor: '#EAB308' }]}>
                        <Text style={[boardStyles.flagPillText, { color: '#000' }]}>{stats.yellowFlags} Yellow</Text>
                      </View>
                    )}
                    {stats.greenFlags > 0 && (
                      <View style={[boardStyles.flagPill, { backgroundColor: '#22C55E' }]}>
                        <Text style={boardStyles.flagPillText}>{stats.greenFlags} Green</Text>
                      </View>
                    )}
                    {stats.inProgress > 0 && (
                      <View style={[boardStyles.flagPill, { backgroundColor: addColorOpacity(tokens.color.warning, 0.2) }]}>
                        <Text style={[boardStyles.flagPillText, { color: tokens.color.warning || '#F59E0B' }]}>{stats.inProgress} In Progress</Text>
                      </View>
                    )}
                    {stats.notApplicable > 0 && (
                      <View style={[boardStyles.flagPill, { backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1) }]}>
                        <Text style={[boardStyles.flagPillText, { color: addColorOpacity(tokens.color.onSurface, 0.5) }]}>{stats.notApplicable} N/A</Text>
                      </View>
                    )}
                  </View>
                </View>
              ) : (
                // Horizontal layout for larger screens
                <>
                  <View style={boardStyles.moduleHeaderLeft}>
                    <View style={boardStyles.moduleIconContainer}>
                      <Ionicons 
                        name={moduleIcon as any} 
                        size={isMobile ? 20 : 24} 
                        color={tokens.color.primary} 
                      />
                    </View>
                    <View style={boardStyles.moduleTitleContainer}>
                      <Text style={boardStyles.moduleTitle}>{moduleName}</Text>
                      <Text style={boardStyles.moduleSubtitle}>
                        {stats.complete}/{stats.applicable} complete
                        {stats.inProgress > 0 && ` • ${stats.inProgress} in progress`}
                        {stats.notApplicable > 0 && ` • ${stats.notApplicable} N/A`}
                      </Text>
                    </View>
                  </View>
                  <View style={boardStyles.moduleHeaderRight}>
                    {/* Flag summary badges */}
                    <View style={boardStyles.flagSummary}>
                      {stats.bigRedFlags > 0 && (
                        <View style={[boardStyles.flagPill, { backgroundColor: '#DC2626' }]}>
                          <Text style={boardStyles.flagPillText}>{stats.bigRedFlags}</Text>
                        </View>
                      )}
                      {stats.redFlags > 0 && (
                        <View style={[boardStyles.flagPill, { backgroundColor: '#EF4444' }]}>
                          <Text style={boardStyles.flagPillText}>{stats.redFlags}</Text>
                        </View>
                      )}
                      {stats.yellowFlags > 0 && (
                        <View style={[boardStyles.flagPill, { backgroundColor: '#EAB308' }]}>
                          <Text style={[boardStyles.flagPillText, { color: '#000' }]}>{stats.yellowFlags}</Text>
                        </View>
                      )}
                      {stats.greenFlags > 0 && (
                        <View style={[boardStyles.flagPill, { backgroundColor: '#22C55E' }]}>
                          <Text style={boardStyles.flagPillText}>{stats.greenFlags}</Text>
                        </View>
                      )}
                    </View>
                    {/* Progress bar */}
                    <View style={boardStyles.progressContainer}>
                      <View style={boardStyles.progressBar}>
                        <View 
                          style={[
                            boardStyles.progressFill, 
                            { width: `${stats.applicable > 0 ? (stats.complete / stats.applicable) * 100 : 0}%` }
                          ]} 
                        />
                      </View>
                    </View>
                    <Ionicons 
                      name={isExpanded ? 'chevron-up' : 'chevron-down'} 
                      size={20} 
                      color={tokens.color.onSurface} 
                    />
                  </View>
                </>
              )}
            </Pressable>

            {/* Module Content */}
            {isExpanded && (
              <View style={boardStyles.moduleContent}>
                {Object.entries(categories).sort().map(([categoryKey, categoryItems]) => {
                  const categoryFullKey = `${moduleKey}-${categoryKey}`;
                  const isCategoryExpanded = expandedCategories.has(categoryFullKey);
                  const categoryName = categoryKey.replace(/_/g, ' ');
                  const categoryStats = calculateModuleStats(categoryItems);

                  // Determine category status color (using applicable count, excluding N/A)
                  const categoryProgress = categoryStats.applicable > 0 
                    ? categoryStats.complete / categoryStats.applicable 
                    : 0;
                  const hasRedFlags = categoryStats.bigRedFlags + categoryStats.redFlags > 0;
                  const allNA = categoryStats.applicable === 0 && categoryStats.notApplicable > 0;
                  const categoryStatusColor = allNA
                    ? addColorOpacity(tokens.color.onSurface, 0.4)  // Grayed out if all N/A
                    : hasRedFlags 
                      ? '#EF4444' 
                      : categoryProgress === 1 
                        ? '#22C55E' 
                        : categoryProgress > 0 
                          ? '#F59E0B' 
                          : addColorOpacity(tokens.color.onSurface, 0.3);

                  return (
                    <View key={categoryKey} style={boardStyles.categorySection}>
                      {/* Category Header */}
                      <Pressable
                        onPress={() => toggleCategory(categoryFullKey)}
                        style={({ pressed }) => [
                          boardStyles.categoryHeader,
                          pressed && boardStyles.categoryHeaderPressed,
                        ]}
                      >
                        <View style={boardStyles.categoryHeaderLeft}>
                          {/* Status indicator circle */}
                          <View style={[boardStyles.categoryStatusDot, { backgroundColor: categoryStatusColor }]}>
                            {categoryProgress === 1 && (
                              <Ionicons name="checkmark" size={10} color="#fff" />
                            )}
                          </View>
                          <Ionicons 
                            name={isCategoryExpanded ? 'chevron-down' : 'chevron-forward'} 
                            size={16} 
                            color={tokens.color.onSurface} 
                          />
                          <Text style={boardStyles.categoryTitle} numberOfLines={1}>{categoryName}</Text>
                        </View>
                        <View style={boardStyles.categoryHeaderRight}>
                          <Text style={boardStyles.categoryCount}>
                            {categoryStats.complete}/{categoryStats.applicable}
                            {categoryStats.notApplicable > 0 && ` (${categoryStats.notApplicable} N/A)`}
                          </Text>
                          {categoryStats.bigRedFlags + categoryStats.redFlags > 0 && (
                            <View style={[boardStyles.miniFlag, { backgroundColor: '#EF4444' }]} />
                          )}
                          {categoryStats.yellowFlags > 0 && (
                            <View style={[boardStyles.miniFlag, { backgroundColor: '#EAB308' }]} />
                          )}
                        </View>
                      </Pressable>

                      {/* Category Items */}
                      {isCategoryExpanded && (
                        <View style={boardStyles.categoryItems}>
                          {categoryItems.map((item) => (
                            <CompactChecklistItem
                              key={item.id}
                              item={item}
                              portfolioId={portfolioId}
                              propertyId={propertyId}
                              isExpanded={expandedItem === item.id}
                              onToggleExpand={() => {
                                LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
                                setExpandedItem(expandedItem === item.id ? null : item.id);
                              }}
                              onUpdate={async (updates) => {
                                await onUpdateItem(item.id, updates);
                              }}
                              availablePartners={availablePartners}
                              loading={loading}
                              tokens={tokens}
                              utils={utils}
                              isMobile={isMobile}
                              isSmallMobile={isSmallMobile}
                            />
                          ))}
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            )}
          </Card>
        );
      })}
    </View>
  );
}

// Compact checklist item - shows title and status, expands to full controls
interface CompactChecklistItemProps {
  item: DueDiligenceItem;
  portfolioId: number;
  propertyId: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (updates: {
    status?: ItemStatus;
    flag?: FlagType | null;
    notes?: string;
    dataFields?: Record<string, any>;
    assignedTo?: number[];
  }) => Promise<void>;
  availablePartners: Array<{ id: number; email: string; display_name?: string }>;
  loading: boolean;
  tokens: ReturnType<typeof useStyles>['tokens'];
  utils: ReturnType<typeof useStyles>['utils'];
  isMobile: boolean;
  isSmallMobile: boolean;
}

function CompactChecklistItem({
  item,
  portfolioId,
  propertyId,
  isExpanded,
  onToggleExpand,
  onUpdate,
  availablePartners,
  loading,
  tokens,
  utils,
  isMobile,
  isSmallMobile,
}: CompactChecklistItemProps) {
  const safeStatus = item?.status || 'pending';
  
  const statusIcons: Record<ItemStatus, { icon: string; color: string }> = {
    pending: { icon: 'ellipse-outline', color: addColorOpacity(tokens.color.onSurface, 0.4) },
    in_progress: { icon: 'time-outline', color: tokens.color.warning || '#EAB308' },
    complete: { icon: 'checkmark-circle', color: tokens.color.success },
    not_applicable: { icon: 'remove-circle-outline', color: addColorOpacity(tokens.color.onSurface, 0.3) },
  };

  const statusConfig = statusIcons[safeStatus] || statusIcons.pending;

  return (
    <View style={{
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.md,
      borderWidth: 1,
      borderColor: isExpanded ? tokens.color.primary : tokens.color.border,
      overflow: 'hidden',
    }}>
      {/* Compact Header */}
      <Pressable
        onPress={onToggleExpand}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          padding: isMobile ? utils.spacing.sm : utils.spacing.md,
          gap: utils.spacing.sm,
          backgroundColor: pressed ? addColorOpacity(tokens.color.onSurface, 0.05) : 'transparent',
        })}
      >
        <Ionicons 
          name={statusConfig.icon as any} 
          size={20} 
          color={statusConfig.color} 
        />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text 
            style={{
              fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
              color: safeStatus === 'not_applicable' 
                ? addColorOpacity(tokens.color.onSurface, 0.5)
                : tokens.color.onSurface,
              fontFamily: utils.typography.body.fontFamily,
              textDecorationLine: safeStatus === 'complete' ? 'line-through' : 'none',
            }}
            numberOfLines={isExpanded ? undefined : 1}
          >
            {item.title}
          </Text>
        </View>
        {item.flag && (
          <FlagBadge flag={item.flag} size="sm" showLabel={false} />
        )}
        {item.notes && (
          <Ionicons 
            name="document-text-outline" 
            size={16} 
            color={addColorOpacity(tokens.color.onSurface, 0.5)} 
          />
        )}
        <Ionicons 
          name={isExpanded ? 'chevron-up' : 'chevron-down'} 
          size={16} 
          color={addColorOpacity(tokens.color.onSurface, 0.5)} 
        />
      </Pressable>

      {/* Expanded Content */}
      {isExpanded && (
        <View style={{
          padding: utils.spacing.md,
          borderTopWidth: 1,
          borderTopColor: tokens.color.border,
          backgroundColor: addColorOpacity(tokens.color.onSurface, 0.02),
        }}>
          <ChecklistItem
            item={item}
            portfolioId={portfolioId}
            propertyId={propertyId}
            onUpdate={onUpdate}
            availablePartners={availablePartners}
            loading={loading}
          />
        </View>
      )}
    </View>
  );
}

function makeBoardStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    container: {
      width: '100%',
      gap: utils.spacing.md,
    },
    controlsRow: {
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      gap: utils.spacing.sm,
      marginBottom: utils.spacing.md,
    },
    summaryInfo: {},
    summaryText: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    controlsButtons: {
      flexDirection: 'row',
      gap: utils.spacing.md,
    },
    controlButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      paddingVertical: utils.spacing.xs,
      paddingHorizontal: utils.spacing.sm,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    controlButtonText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    moduleCard: {
      padding: 0,
      overflow: 'hidden',
    },
    moduleHeader: {
      flexDirection: isSmallMobile ? 'column' : 'row',
      alignItems: isSmallMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      padding: isSmallMobile ? utils.spacing.sm : isMobile ? utils.spacing.md : utils.spacing.lg,
      backgroundColor: tokens.color.surface,
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    moduleHeaderPressed: {
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.05),
    },
    // Stacked mobile layout styles
    moduleHeaderRow1: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      marginBottom: utils.spacing.sm,
    },
    progressContainerFull: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      marginBottom: utils.spacing.sm,
    },
    progressText: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      fontWeight: utils.typography.weights.medium,
    },
    flagRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.xs,
    },
    // Horizontal layout styles
    moduleHeaderLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.md,
      flex: 1,
      minWidth: 0,
    },
    moduleIconContainer: {
      width: isSmallMobile ? 32 : isMobile ? 40 : 48,
      height: isSmallMobile ? 32 : isMobile ? 40 : 48,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    moduleTitleContainer: {
      flex: 1,
      minWidth: 0,
    },
    moduleTitle: {
      fontSize: isSmallMobile ? utils.typography.sizes.sm : isMobile ? utils.typography.sizes.base : utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
      flex: 1,
    },
    moduleSubtitle: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      marginTop: 2,
    },
    moduleHeaderRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.md,
    },
    flagSummary: {
      flexDirection: 'row',
      gap: utils.spacing.xs,
    },
    flagPill: {
      paddingHorizontal: isSmallMobile ? utils.spacing.sm : utils.spacing.xs,
      paddingVertical: 2,
      borderRadius: utils.radius.sm,
      minWidth: isSmallMobile ? undefined : 20,
      alignItems: 'center',
    },
    flagPillText: {
      fontSize: isSmallMobile ? 10 : utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.bold,
      color: '#fff',
      fontFamily: utils.typography.body.fontFamily,
    },
    progressContainer: {
      width: isMobile ? 40 : 60,
    },
    progressBar: {
      height: 4,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
      borderRadius: 2,
      overflow: 'hidden',
      flex: 1,
    },
    progressFill: {
      height: '100%',
      backgroundColor: tokens.color.success,
      borderRadius: 2,
    },
    moduleContent: {
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
    },
    categorySection: {
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    categoryHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.md,
      paddingLeft: isMobile ? utils.spacing.md : utils.spacing.lg,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.02),
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    categoryHeaderPressed: {
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.05),
    },
    categoryHeaderLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      flex: 1,
      minWidth: 0,
    },
    categoryStatusDot: {
      width: 16,
      height: 16,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    categoryTitle: {
      fontSize: isSmallMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      textTransform: 'capitalize',
      flex: 1,
    },
    categoryHeaderRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
    },
    categoryCount: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.5),
      fontFamily: utils.typography.body.fontFamily,
    },
    miniFlag: {
      width: 8,
      height: 8,
      borderRadius: 4,
    },
    categoryItems: {
      padding: utils.spacing.sm,
      paddingLeft: isMobile ? utils.spacing.sm : utils.spacing.lg,
      gap: utils.spacing.xs,
      backgroundColor: tokens.color.background,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: utils.spacing['2xl'],
    },
    emptyText: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onBackground,
      marginTop: utils.spacing.md,
      fontFamily: utils.typography.body.fontFamily,
    },
    emptySubtext: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      textAlign: 'center',
    },
  });
}
